import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, DollarSign, Building2, Calendar, Users, Stethoscope,
  Shield, Settings, Menu, LogOut, ArrowLeft,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/revenue", label: "Revenue", icon: DollarSign },
  { to: "/admin/providers", label: "Providers", icon: Building2 },
  { to: "/admin/bookings", label: "Bookings", icon: Calendar },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/services", label: "Services", icon: Stethoscope },
  { to: "/admin/compliance", label: "Compliance", icon: Shield },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const SidebarContent = (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-white/10">
        <div className="font-heading text-lg font-extrabold text-white">SarvaSeva</div>
        <div className="text-[11px] uppercase tracking-widest text-[#2A9D8F] font-bold">Admin Console</div>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {NAV.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-[#0D5C75] text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
              <Icon size={18} /> {n.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-white/10 space-y-1">
        <button onClick={() => navigate("/")} data-testid="admin-back-site"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/70 hover:bg-white/5 hover:text-white">
          <ArrowLeft size={18} /> Back to site
        </button>
        <button onClick={() => { logout(); navigate("/login"); }} data-testid="admin-logout"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/70 hover:bg-white/5 hover:text-white">
          <LogOut size={18} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex" data-testid="admin-layout">
      <aside className="hidden lg:block w-64 shrink-0 bg-[#111827] fixed inset-y-0 left-0 z-30">{SidebarContent}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-[#111827]">{SidebarContent}</aside>
        </div>
      )}
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b flex items-center gap-3 px-4 py-3">
          <button onClick={() => setOpen(true)} data-testid="admin-menu-btn"><Menu size={22} /></button>
          <span className="font-heading font-bold text-[#111827]">Admin Console</span>
          <span className="ml-auto text-xs text-muted-foreground truncate max-w-[120px]">{user?.email}</span>
        </header>
        <main className="p-5 sm:p-8 max-w-[1400px] mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
